import type { PageTemplate } from '@packages/recommendations';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { getEcState } from '@packages/shared/src/utils/ecState/ecState';
import { Suspense } from 'react';
import type { Distance, ParagraphLibraryType, ParagraphType } from '../../../interfaces/components';
import { ComponentTypes } from '../../../interfaces/components';
import { useCookieConsentCategoryManager } from '../../hooks/useCookieConsentCategoryManager';
import { useFilterTimeControl } from '../../hooks/useFilterTimeControl';
import { getAllAudiences } from '../../utils/audiences';
import { classes } from '../../utils/classes';
import { createFilterForCurrentAudiences } from '../../utils/filterResult';
import { getComponent } from './componentRegistry';

const styleDistance = (distance?: Distance) => ({
  ...(distance === 'variation-1' && {
    paddingInline: { xs: 3, lg: 0 },
  }),
  ...(distance === 'fullwidth' && {
    width: '100vw',
    left: '50%',
    transform: 'translate(-50vw, 0)',
    position: 'relative',
  }),
});

const classesDistance = (distance?: Distance) => {
  if (distance === 'variation-1') {
    return 'px-3 lg:px-0';
  }
  if (distance === 'fullwidth') {
    return 'relative left-1/2 w-screen -translate-x-1/2! translate-y-0';
  }
  return '';
};

type ComponentRecognizerProps = {
  item: ParagraphType;
  pageTemplate?: PageTemplate;
  recoIndex?: number;
  token?: string;
  testDate?: string;
  className?: string;
  style?: React.CSSProperties;
};

export const ComponentRecognizer = ({
  /** The data for the single paragraph you want to render */
  item,
  /** current page you are on (needed for tracking purposes) */
  pageTemplate,
  /** For tracking: if this component is a Reco, please specify which reco it is on the page (starting with 0) */
  recoIndex = 0,
  token,
  testDate,
  className,
  style,
}: ComponentRecognizerProps) => {
  const { getCookies } = useCookies();
  const cookies = getCookies();
  const audiences = getAllAudiences(getEcState(cookies));
  const filterTime = useFilterTimeControl(testDate);

  const filters = [createFilterForCurrentAudiences(audiences), filterTime];
  const showContent = filters.every((filter) => filter(item));

  if (showContent) {
    const Component = getComponent(item.type);
    if (Component) {
      return (
        <Component
          data={item}
          sx={{
            ...('field_distance' in item &&
              (item.field_distance === 'variation-1' || item.field_distance === 'fullwidth') &&
              styleDistance(item.field_distance)),
          }}
          className={classes(
            'field_distance' in item &&
              (item.field_distance === 'variation-1' || item.field_distance === 'fullwidth')
              ? classesDistance(item.field_distance)
              : undefined,
            className,
          )}
          pageTemplate={pageTemplate}
          recoIndex={recoIndex}
          token={token}
          key={item.id}
          style={style}
        />
      );
    }

    if (item.type === ComponentTypes.FromLibrary) {
      return (
        <>
          {(item as ParagraphLibraryType)?.paragraphs_library_item?.map((libItem) =>
            libItem?.paragraph?.map((libParagraphItem: ParagraphType) => (
              <ComponentRecognizer key={item.id} item={libParagraphItem} token={token} />
            )),
          )}
        </>
      );
    }
  }
  return null;
};

/**
 * Renders an array of cms paragraphs using the correct components from the component recognizer
 */
export const CmsComponents = ({
  paragraphs = [],
  pageTemplate,
  token,
}: {
  /** array of all paragraphs that are to be rendered */
  paragraphs: ParagraphType[];
  /** current page you are on (needed for tracking purposes) */
  pageTemplate: PageTemplate;
  token?: string;
}) => {
  let recoIndex = 0;
  useCookieConsentCategoryManager();

  return (
    <>
      {paragraphs.map((p) => (
        <Suspense key={p.id}>
          <ComponentRecognizer
            item={p}
            pageTemplate={pageTemplate}
            // if this paragraph is a recoslider, pass the reco index and increase it after that
            // eslint-disable-next-line no-plusplus
            {...(p.type === ComponentTypes.RecoPrudsys && { recoIndex: recoIndex++ })}
            token={token}
          />
        </Suspense>
      ))}
    </>
  );
};
